import { FC, useState, Ref } from "react";
import "./ProjectDetailsHeader.scss";
import {
  Alert,
  AlertActionCloseButton,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  ClipboardCopy,
  Dropdown,
  DropdownItem,
  DropdownItemProps,
  DropdownList,
  Grid,
  GridItem,
  MenuToggle,
  MenuToggleElement,
  Split,
  SplitItem,
  Content,
  TooltipProps,
  capitalize,
  Badge,
  Flex,
  FlexItem
} from "@patternfly/react-core";
import { CircleIcon, ExternalLinkAltIcon } from "@patternfly/react-icons";
import { isEmpty } from "lodash";
import Env from "../../../utils/env";
import Uri from "jsuri";
import {
  useSystemStatus,
  useIncidentDetails,
  useMaintenanceDetails,
  incidentDetailsQueryKey, maintenanceDetailsQueryKey
} from "../../../queries/getSystemStatus";
import { patchProjectDetails } from "../../../api/pyxis";
import { useMutation } from "@tanstack/react-query";
import { ErrorPopup, SuccessPopup, useAlertContext } from "../AlertComponent/AlertContext";
import {
  ECatalogVisibility,
  getProjectTypeData,
  ProjectCertificationStatus,
  ProjectContainerKeys,
  ProjectContainerLinks,
  ProjectContainerTypes,
  projectDisplayNames,
  ProjectKeys,
  ProjectRedhatKeys,
  ProjectTypes,
  SystemStatus,
  SystemStatusAlertVariant
} from "../../../constants/project";
import { usePublishContext } from "../../CertWorkflowApp/PublishContext";
import { IsOperatorBundleProject, IsRHELNonContainerized } from "../../../utils/projectUtils";
import { useControllerContext } from "@rhc-shared-components/control-wrapper";
import { useQueryClient } from "@tanstack/react-query";
import { ProductKeys } from "../../../constants/products";
import { useParams } from "react-router";
import { CWRouteParams } from "../../../constants/router";
import Cookies from "js-cookie";
import { syncTaticIds } from "../../../api/pfs";
import { TROUBLE_SHOOTING_URL } from "../../../constants/common";
import { projectDetailsQueryKey, useProjectDetails } from "../../../queries/getProjectDetails";
import { useProductDetails as useProductDetailsHook } from "../../../queries/getProductDetails";
import { useRepoDetails } from "../../../queries/getRepoDetails";

enum EStatusPageStatus {
  "investigating" = "Investigating",
  "identified" = "Identified",
  "monitoring" = "Monitoring",
  "resolved"  = "Resolved",
  "scheduled" = "Scheduled",
  "in_progress" = "In Progress",
  "verifying" = "Verifying",
  "completed" = "Completed"
}

const ProjectDetailsHeader: FC = () => {
  const [selectActionDropdownText, setSelectActionDropdownText] =
    useState('Actions');
  const [isActionDropdownOpen, toggleActionDropdown] =
    useState<boolean>(false);
  const { productId, id: projectId } = useParams<CWRouteParams>();
  const queryClient = useQueryClient();
  const { data: projectDetails } = useProjectDetails(projectId);
  const { data: productInfo } = useProductDetailsHook(productId, productId !== 'view');
  const repoAPIUrl = projectDetails?.[ProjectKeys.container]?.[ProjectContainerKeys.links][ProjectContainerLinks.repositories]?.href;
  const { data: repoDetailsResponse } =  useRepoDetails(repoAPIUrl, !isEmpty(projectDetails) && IsOperatorBundleProject(projectDetails));
  const repoDetails = repoDetailsResponse?.data || [];
  const { setAlertState } = useAlertContext();
  const patchMutation = useMutation({
    mutationFn: ({ id, projectData }: { id: string; projectData: any }) =>
      patchProjectDetails(id, projectData)
  });
  const iconClassName = 'project-details-header__secondary__info__icon';
  const { publishState } = usePublishContext();
  const { isReadOnly } = useControllerContext();
  const isArchiveDisabled = () =>
    publishState ||
    projectDetails?.[ProjectKeys.certificationStatus] ===
    ProjectCertificationStatus.published;
  const privateCertOptions = projectDetails[ProjectKeys.type] === ProjectTypes.Containers ? [{
      value: 'update_catalog_visibility',
      text: `Make ${(projectDetails?.[ProjectKeys.catalogVisibility] === ECatalogVisibility.hidden) ?
        "public" : "private"}`,
      isDisabled: projectDetails?.[ProjectKeys.projectStatus]?.toLowerCase() === "archived" || isReadOnly || patchMutation.isPending,
    }]:[];
  let actionsOptions = [
    {
      value: 'open_support_case',
      text: 'Open Support Case',
      isDisabled: false,
    },
    {
      value: 'archive_project',
      text: 'Archive Component',
      isDisabled: isArchiveDisabled() || isReadOnly || patchMutation.isPending,
    },
    ...privateCertOptions
  ];

  if (IsOperatorBundleProject(projectDetails)) {
    const option = {
      value: 'remove_from_operator_hub',
      text: 'Remove from Operator Hub',
      isDisabled:
        !IsOperatorBundleProject(
          (projectDetails[ProjectKeys.container] || {})[
            ProjectContainerKeys.type
            ]
        ) && !(!isEmpty(repoDetails) && repoDetails?.length > 0),
    };

    actionsOptions = [...actionsOptions, { ...option }];
  }

  const { data: systemStatus } = useSystemStatus();
  const { data: incidentDetails, isLoading: isStatusFetching } = useIncidentDetails();
  const { data: maintenanceDetails, isLoading: fetchingMaintenances } = useMaintenanceDetails();
  const onToggle = () => {
    toggleActionDropdown(!isActionDropdownOpen);
  };

  const archiveProject = async () => {
    const requestData = {
      _id: projectDetails[ProjectKeys.id],
      project_status: 'archived',
    };
    try {
      const response = await patchMutation.mutateAsync({ id: projectDetails[ProjectKeys.id], projectData: requestData });
      await syncTaticIds(projectDetails[ProjectKeys.id], "CERTIFICATION_STATUS_ARCHIVED");
      SuccessPopup(
        setAlertState,
        `Archived ${response.name} project successfully`
      );
      window.open(
        `${Env.productListingUrl}/components?component_status=archived`,
        '_self'
      );
    } catch (error: any) {
      ErrorPopup(setAlertState, error.message);
    }
  };

  const flipCatalogVisibility = async () => {
    const requestData = {
      [ProjectKeys.id]: projectDetails[ProjectKeys.id],
      [ProjectKeys.catalogVisibility]: `${projectDetails?.[ProjectKeys.catalogVisibility] === ECatalogVisibility.hidden ? ECatalogVisibility.visible : ECatalogVisibility.hidden}`,
    };
    try {
      const response = await patchMutation.mutateAsync({ id: projectDetails[ProjectKeys.id], projectData: requestData });
      queryClient.setQueryData(projectDetailsQueryKey, response);
      SuccessPopup(
        setAlertState,
        `Component visibility updated successfully`
      );
    } catch (error: any) {
      ErrorPopup(setAlertState, error.message);
    }
  };

  const onSelect = (event) => {
    const selectedItem = actionsOptions.find(
      (el) => el.text === event.target.textContent,
    );
    setSelectActionDropdownText(selectedItem.text);
    toggleActionDropdown(!isActionDropdownOpen);

    if (event.target.textContent === 'Archive Component') {
      archiveProject();
    }
    if (event.target.textContent === 'Make public' || event.target.textContent === 'Make private') {
      flipCatalogVisibility();
    }
    if (
      event.target.textContent === 'Open Support Case' ||
      event.target.textContent === 'Remove from Operator Hub'
    ) {
      let TPSDprojectType;
      let supportUrl;

      // TPSD currently only supports ->
      // [Container Image, Operator Bundle Image, Helm Chart] types
      // Ref: https://red.ht/3zKJCsE
      switch (projectDetails[ProjectKeys.type]) {
        case ProjectTypes.Containers: {
          TPSDprojectType =
            projectDetails[ProjectKeys.container][ProjectContainerKeys.type] ===
            ProjectContainerTypes.OPERATOR_BUNDLE_IMAGE
              ? 'Operator Bundle Image'
              : 'Container Image';
          break;
        }
        case ProjectTypes.HelmChart:
          TPSDprojectType = 'Helm Chart';
          break;
        default:
          TPSDprojectType = null;
      }
      supportUrl = new Uri(Env.ConnectPortalUrls[Env.getEnvName()]).setPath(
        '/support/partner-acceleration-desk/#/case/new'
      );

      if (TPSDprojectType) {
        supportUrl = supportUrl
          .addQueryParam('type', 'CERT')
          .addQueryParam('source', 'connect')
          .addQueryParam('cert_project_id', projectDetails[ProjectKeys.id])
          .addQueryParam('cert_project_type', TPSDprojectType);
      }

      if (event.target.text === 'Remove from Operator Hub') {
        supportUrl = supportUrl.addQueryParam(
          'description',
          'My company would like to remove our registry from the Operator Hub'
        );
        supportUrl.addQueryParam(
          'summary',
          `Request to remove ${repoDetails?.[0].repository} from Operator Hub`
        );
      }

      window.open(supportUrl, '_blank');
    }
  };

  const getActions = () =>
    actionsOptions.map((item) => {
      const dropdownProps: DropdownItemProps = {
        id: item.value,
        isDisabled: item.isDisabled,
        style: {
          pointerEvents: 'auto',
          cursor: 'default',
          color: `var(${item.isDisabled ? '--pf-v6-c-menu__item--m-disabled--Color' : '--pf-v6-c-menu__item--Color'})`,
        },
      };
      const toolTipProps: TooltipProps = {
        content: null,
        position: 'bottom',
      };
      if (item.isDisabled) {
        if (item.value === 'open_support_case') {
          // Adding popup info for the archive project
          const hasImages = [
            ProjectTypes.Containers,
            ProjectTypes.openStackAppContainer,
          ].includes(projectDetails[ProjectKeys.type]);
          toolTipProps.content = hasImages
            ? 'All images must be unpublished to archive project'
            : 'Project must be unpublished to archive project';
        } else if (item.value === 'remove_from_operator_hub') {
          toolTipProps.content = 'This project doesn\'t have a published operator';
        }
      }
      /* eslint-disable react/jsx-props-no-spreading */
      return (
        <DropdownItem
          {...dropdownProps}
          {...(toolTipProps.content !== null ? { tooltipProps: toolTipProps } : {})}
          key={item.value}
        >
          {item.text}
        </DropdownItem>
      );
    });
  const params = useParams<CWRouteParams>();
  const getSubheading = () => {
    return getProjectTypeData(projectDetails) === 'RHEL' && projectDetails[ProjectKeys.redhat]
    && projectDetails[ProjectKeys.redhat][ProjectRedhatKeys.productVersion] ?
      `Red Hat Enterprise Linux ${projectDetails[ProjectKeys.redhat][ProjectRedhatKeys.productVersion]}
    ${projectDetails[ProjectKeys.container] ? 'Containerized' : 'Non-Containerized'} Application` :
      projectDisplayNames[getProjectTypeData(projectDetails)]
  }
  const isContainerComponent = [
      ProjectTypes.Containers,
      ProjectTypes.openStackInfraContainer,
      ProjectTypes.openStackAppContainer,
      ProjectTypes.RHEL
    ].includes(projectDetails[ProjectKeys.type]) &&
    !IsOperatorBundleProject(projectDetails) &&
    !IsRHELNonContainerized(projectDetails);
  return (
    <Grid className="project-details-header" data-analytics-category="Component Details Header">
      {!isEmpty(incidentDetails) &&
        incidentDetails.enabled &&
        !isStatusFetching && (
          <GridItem span={10} offset={1} className="project-details-header__alert">
            <Alert
              isInline={true}
              title={`${incidentDetails.name} Incident`}
              variant={
                SystemStatusAlertVariant[incidentDetails.componentStatus]
              }
              actionClose={
                <AlertActionCloseButton
                  onClose={() => {
                    Cookies.set(`markedDone${incidentDetails?.id}`, 'done', {
                      expires: 7,
                    });
                    queryClient.setQueryData(incidentDetailsQueryKey, { ...incidentDetails, enabled: false });
                  }}
                />
              }>
              {`${capitalize(EStatusPageStatus?.[incidentDetails.incidentStatus] || incidentDetails.incidentStatus)} - ${incidentDetails.description} - ${incidentDetails.updatedAt}`}
              &nbsp;
              <Button
                target="_blank"
                component="a"
                href={incidentDetails.link}
                variant="link"
                isInline>
                View incident report
              </Button>
            </Alert>
          </GridItem>
        )}
      {!isEmpty(maintenanceDetails) &&
        maintenanceDetails.enabled &&
        !fetchingMaintenances && (
          <GridItem span={10} offset={1} className="project-details-header__alert">
            <Alert
              isInline={true}
              title={`${maintenanceDetails.name} Scheduled for ${maintenanceDetails.scheduledFor} - ${maintenanceDetails.scheduledUntil}`}
              variant={
                SystemStatusAlertVariant[maintenanceDetails.incidentStatus]
              }
              actionClose={
                <AlertActionCloseButton
                  onClose={() => {
                    Cookies.set(`markedDone${maintenanceDetails?.id}`, 'done', {
                      expires: 7,
                    });
                    queryClient.setQueryData(maintenanceDetailsQueryKey, {
                      ...maintenanceDetails,
                      enabled: false,
                    });
                  }}
                />
              }>
              <Content component="p">
                {`${capitalize(EStatusPageStatus?.[maintenanceDetails.incidentStatus] || maintenanceDetails.incidentStatus )} - ${maintenanceDetails.description}`}
              </Content>
              <Content component="small">{`Posted on - ${maintenanceDetails.updatedAt}   `}</Content>
              <Button
                target="_blank"
                component="a"
                href={maintenanceDetails.link}
                variant="link"
                isInline>
                View maintenance
              </Button>
            </Alert>
          </GridItem>
        )}
      <GridItem span={10} offset={1} className="project-details-header__breadcrumb">
        <Breadcrumb>
          {params?.productId !== Env.productIdStatic ?
            <BreadcrumbItem to={`${Env.productListingUrl}/products`}>
              My Products
            </BreadcrumbItem>:
            <BreadcrumbItem to={`${Env.productListingUrl}/components`}>
              My Components
            </BreadcrumbItem>
          }
          {params?.productId !== Env.productIdStatic &&
            <BreadcrumbItem to={`${Env.productListingUrl}/products/${params?.productId}/components`}>
              {productInfo?.[ProductKeys.productName]}
            </BreadcrumbItem>
          }
          <BreadcrumbItem isActive={true}>{projectDetails[ProjectKeys.name]}</BreadcrumbItem>
        </Breadcrumb>
      </GridItem>
      <GridItem span={10} offset={1} className="project-details-header__primary">
        <Split className="project-details-header__primary__info">
          <SplitItem isFilled>
            <Split className="project-details-header__primary__info--align">
              <SplitItem>
                <Content className="project-details-header__primary__info--details">
                  <Content component="p" className="project-details-header__primary__info--name">
                    {projectDetails[ProjectKeys.name]}&nbsp;
                    {projectDetails?.[ProjectKeys.catalogVisibility] === ECatalogVisibility.hidden && <Badge isRead>Private</Badge>}
                  </Content>
                  <Content component="p" className="project-details-header__primary__info--type">
                    {getSubheading()}{' '}
                    component
                  </Content>
                </Content>
              </SplitItem>
            </Split>
          </SplitItem>
          <SplitItem>
            <Flex spaceItems={{ default: 'spaceItemsNone' }}>
              <FlexItem>
                <Content>
                  <Content component="p"><b>Component PID:</b></Content>
                </Content>
              </FlexItem>
              <FlexItem style={{
                paddingRight: '1em'
              }}>
                <ClipboardCopy style={{ background: "transparent" }} variant="inline-compact">{params?.id}</ClipboardCopy>
              </FlexItem>
            </Flex>
          </SplitItem>
          <SplitItem>
            <Dropdown
              onSelect={onSelect}
              shouldFocusToggleOnSelect
              onOpenChange={() => toggleActionDropdown(!isActionDropdownOpen)}
              toggle={(toggleRef: Ref<MenuToggleElement>) => (
                <MenuToggle ref={toggleRef} onClick={onToggle} isExpanded={isActionDropdownOpen} isFullWidth>
                  {selectActionDropdownText}
                </MenuToggle>
              )}
              isOpen={isActionDropdownOpen}
            >
              <DropdownList>
                {getActions()}
              </DropdownList>
            </Dropdown>
          </SplitItem>
        </Split>
      </GridItem>
      <GridItem span={10} offset={1}>
        <Split className="project-details-header__troubleshooting_guide">
          <SplitItem style={{
            minHeight: '2em'
          }}>
            {isContainerComponent &&
              <Button
                style={{
                  paddingRight: 0,
                  marginRight: 0
                }}
                component="a"
                isInline={true}
                href={TROUBLE_SHOOTING_URL}
                target={"_blank"}
                variant={"link"}
                icon={<ExternalLinkAltIcon />}
                iconPosition="end">
                Troubleshoot component
              </Button>
            }
          </SplitItem>
        </Split>
      </GridItem>
      <GridItem span={10} offset={1} className="project-details-header__secondary">
        <Split className="project-details-header__secondary__info">
          <SplitItem>
            <Split>
              {!isEmpty(systemStatus) && (
                <SplitItem>
                  <Content style={isContainerComponent ? {} : {
                    paddingRight: 0,
                    marginRight: 0
                  }} component="small">
                    <CircleIcon
                      height="8px"
                      width="8px"
                      className={`${iconClassName} ${iconClassName}--${SystemStatusAlertVariant[systemStatus]}`}
                    />
                    {SystemStatus[systemStatus]}
                  </Content>
                </SplitItem>
              )}
              {isContainerComponent && (
                <SplitItem>
                  <Content style={{
                    paddingRight: 0,
                    marginRight: 0
                  }} component="small">
                    <CircleIcon
                      height="8px"
                      width="8px"
                      className={
                        projectDetails?.[ProjectKeys.container]?.[
                          ProjectContainerKeys.autoPublish
                          ]
                          ? `${iconClassName} ${iconClassName}--on`
                          : `${iconClassName} ${iconClassName}--off`
                      }
                    />
                    Auto publish is{' '}
                    {projectDetails?.[ProjectKeys.container]?.[
                      ProjectContainerKeys.autoPublish
                      ]
                      ? 'on'
                      : 'off'}
                  </Content>
                </SplitItem>
              )}
            </Split>
          </SplitItem>
        </Split>
      </GridItem>
    </Grid>
  );
};

export default ProjectDetailsHeader;
